import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage, languages } from '../context/LanguageContext';
import { useTranslation } from '../data/translations';

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const navigate = useNavigate();
  const { lang, setLang } = useLanguage();
  const { t, get } = useTranslation(lang);

  const navItems = [
    { label: get(t.nav.home), path: '/' },
    { label: get(t.nav.projects), path: '/projecten' },
    { label: get(t.nav.team), path: '/team' },
    { label: get(t.nav.ambition), path: '/ambitiedocument' },
    { label: get(t.nav.design), path: '/doordacht-design' },
    { label: get(t.nav.execution), path: '/uitvoering' },
    { label: get(t.nav.contact), path: '/contact' },
  ];

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  return (
    <>
      <header className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${scrolled ? 'py-3' : 'py-5'}`}>
        <div className="flex items-center justify-between px-8 md:px-16">
          {/* Logo */}
          <a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); }} className="relative z-[60]">
            <svg viewBox="0 0 120 60" className="h-12 md:h-14 w-auto" fill="none">
              <circle cx="30" cy="30" r="28" stroke="white" strokeWidth="2.5" fill="none" />
              <path d="M50 8 Q58 2, 54 12" stroke="white" strokeWidth="2.5" fill="none" strokeLinecap="round" />
              <text x="12" y="36" fill="white" fontSize="14" fontWeight="700" fontFamily="Space Grotesk, sans-serif">cobras</text>
            </svg>
          </a>

          <div className="flex items-center gap-4 relative z-[60]">
            {/* Language switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="text-sm font-bold tracking-wider text-white/70 hover:text-white transition-colors px-2 py-1"
              >
                {lang.toUpperCase()}
              </button>
              {langOpen && (
                <div className="absolute top-full right-0 mt-2 py-2 rounded-sm shadow-xl min-w-[120px]" style={{ backgroundColor: '#1a1a1a', border: '1px solid #333' }}>
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLang(l.code); setLangOpen(false); }}
                      className={`block w-full text-left px-4 py-2 text-sm transition-colors ${lang === l.code ? 'text-[#A855F7] font-bold' : 'text-gray-300 hover:text-white hover:bg-white/5'}`}
                    >
                      {l.label} — {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Hamburger */}
            <button
              onClick={() => { setMenuOpen(!menuOpen); setLangOpen(false); }}
              className="flex flex-col gap-[7px] group cursor-pointer"
              aria-label="Menu"
            >
              <span className={`block h-[3px] w-10 rounded-full transition-all duration-500 ${menuOpen ? 'rotate-45 translate-y-[10px] bg-white' : 'bg-[#A855F7]'}`} />
              <span className={`block h-[3px] w-10 rounded-full transition-all duration-500 ${menuOpen ? 'opacity-0 bg-white' : 'bg-[#A855F7]'}`} />
              <span className={`block h-[3px] w-10 rounded-full transition-all duration-500 ${menuOpen ? '-rotate-45 -translate-y-[10px] bg-white' : 'bg-[#A855F7]'}`} />
            </button>
          </div>
        </div>
      </header>

      {/* Menu Overlay */}
      <div className={`fixed inset-0 z-[55] transition-all duration-700 ${menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`} style={{ backgroundColor: '#0a0a0a' }}>
        <div className="flex flex-col items-center justify-center h-full">
          <nav className="flex flex-col items-center gap-5">
            {navItems.map((item, i) => (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  setMenuOpen(false);
                  navigate(item.path);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-3xl md:text-5xl font-bold text-white hover:text-[#A855F7] transition-colors duration-300"
                style={{
                  transform: menuOpen ? 'translateY(0)' : 'translateY(30px)',
                  opacity: menuOpen ? 1 : 0,
                  transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${menuOpen ? i * 60 : 0}ms`,
                }}
              >
                {item.label}
              </a>
            ))}
          </nav>

          {/* Language switcher in menu */}
          <div className="mt-10 flex gap-3" style={{ opacity: menuOpen ? 1 : 0, transition: 'opacity 0.5s ease 0.5s' }}>
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => { setLang(l.code); }}
                className={`px-3 py-1.5 text-sm font-bold transition-all duration-300 border ${lang === l.code ? 'bg-[#A855F7] border-[#A855F7] text-white' : 'border-gray-600 text-gray-400 hover:border-white hover:text-white'}`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <div className="absolute bottom-12 flex gap-8 text-sm text-gray-400" style={{ opacity: menuOpen ? 1 : 0, transition: 'opacity 0.6s ease 0.5s' }}>
            <a href="https://www.facebook.com/cobrasinteriors" target="_blank" rel="noopener noreferrer" className="hover:text-[#A855F7] transition-colors">Facebook</a>
            <a href="https://www.instagram.com/cobras_architects/" target="_blank" rel="noopener noreferrer" className="hover:text-[#A855F7] transition-colors">Instagram</a>
            <a href="https://www.linkedin.com/company/cobras-architects/" target="_blank" rel="noopener noreferrer" className="hover:text-[#A855F7] transition-colors">Linkedin</a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
